import { useEffect, useMemo, useState } from "react";
import { Link } from "react-router-dom";
import { api, formatError } from "@/lib/api";
import { useCountries } from "@/lib/countries";
import { Card } from "./Dashboard";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter,
} from "@/components/ui/dialog";
import { toast } from "sonner";
import { Plus, Power, Download, FastForward, Sparkles } from "lucide-react";

const currentYear = new Date().getFullYear();
const YEARS = Array.from({ length: 7 }, (_, i) => currentYear - 1 + i); // last yr → +5

export default function BankHolidaysPage() {
  const allCountries = useCountries();
  const [companyCountries, setCompanyCountries] = useState(null); // null = loading
  const [country, setCountry] = useState("GB");
  const [year, setYear] = useState(currentYear);
  const [rows, setRows] = useState([]);
  const [open, setOpen] = useState(false);
  const [importOpen, setImportOpen] = useState(false);
  const [importBusy, setImportBusy] = useState(false);
  const [rollBusy, setRollBusy] = useState(false);
  const [form, setForm] = useState({ date: "", name: "" });

  // Load the caller's company.countries so we can scope the picker.
  useEffect(() => {
    api.get("/companies")
      .then(r => {
        const co = (r.data || [])[0] || {};
        setCompanyCountries(co.countries || []);
      })
      .catch(() => setCompanyCountries([]));
  }, []);

  // Filter the global country directory down to those configured on the company.
  // Falls back to the full list when no scope is set yet (defensive).
  const countries = useMemo(() => {
    if (!companyCountries || companyCountries.length === 0) return allCountries;
    const scope = new Set(companyCountries.map(c => String(c).toLowerCase()));
    return allCountries.filter(c =>
      scope.has((c.code || "").toLowerCase()) || scope.has((c.name || "").toLowerCase())
    );
  }, [allCountries, companyCountries]);

  // Auto-pick the first configured country once the scope loads.
  useEffect(() => {
    if (companyCountries && companyCountries.length > 0 && countries.length > 0
        && !countries.find(c => c.code === country)) {
      setCountry(countries[0].code);
    }
  }, [companyCountries, countries, country]);

  const load = async () => {
    const { data } = await api.get("/bank-holidays", { params: { country_code: country, year } });
    setRows(data);
  };
  useEffect(() => { load(); /* eslint-disable-next-line */ }, [country, year]);

  const merged = useMemo(() => {
    const map = new Map();
    for (const r of rows) {
      const existing = map.get(r.date);
      if (!existing || (r.company_id && !existing.company_id)) map.set(r.date, r);
    }
    return [...map.values()].sort((a, b) => a.date.localeCompare(b.date));
  }, [rows]);

  const selectedCountry = countries.find(c => c.code === country);
  const canAutoImport = !!selectedCountry?.auto_import_supported;

  const submit = async (e) => {
    e.preventDefault();
    try {
      await api.post("/bank-holidays", { country_code: country, date: form.date, name: form.name });
      toast.success("Holiday added"); setOpen(false); setForm({ date: "", name: "" }); load();
    } catch (e) { toast.error(formatError(e.response?.data?.detail)); }
  };

  const toggle = async (h) => {
    try { await api.patch(`/bank-holidays/${h.holiday_id}`, { is_active: !h.is_active }); load(); }
    catch (e) { toast.error(formatError(e.response?.data?.detail)); }
  };

  const runImport = async (overwrite) => {
    setImportBusy(true);
    try {
      const { data } = await api.post("/bank-holidays/import", { country_code: country, year, overwrite });
      toast.success(`Imported ${data.imported} holidays · skipped ${data.skipped}`);
      setImportOpen(false); load();
    } catch (e) { toast.error(formatError(e.response?.data?.detail)); }
    finally { setImportBusy(false); }
  };

  const rollForward = async () => {
    setRollBusy(true);
    try {
      const { data } = await api.post(`/bank-holidays/roll-forward?year=${year + 1}`);
      const imported = data.results.reduce((s, r) => s + (r.imported || 0), 0);
      toast.success(`Rolled to ${data.year}: ${imported} holidays across ${data.results.length} countries`);
    } catch (e) { toast.error(formatError(e.response?.data?.detail)); }
    finally { setRollBusy(false); }
  };

  const noCompanyCountries = companyCountries !== null && companyCountries.length === 0;

  if (noCompanyCountries) {
    return (
      <div data-testid="bh-needs-countries">
        <div className="eyebrow">Configuration</div>
        <h1 className="font-display text-4xl mt-1">Bank holidays</h1>
        <div className="mt-6 max-w-xl rounded-xl border border-amber-200 bg-amber-50 p-5">
          <div className="font-medium text-amber-900">Set your operating countries</div>
          <p className="text-sm text-amber-900/80 mt-1">
            Bank holidays already apply automatically based on each employee&apos;s
            country, using our standard calendar. Add your operating countries
            here to import or customise official holiday calendars for those
            countries.
          </p>
          <Link to="/app/settings"
            data-testid="bh-go-to-settings"
            className="inline-flex items-center mt-3 text-sm font-medium text-[#2563EB] hover:underline">
            Open Company Settings →
          </Link>
        </div>
      </div>
    );
  }

  return (
    <div>
      <div className="flex items-end justify-between mb-8 flex-wrap gap-4">
        <div>
          <div className="eyebrow">Configuration</div>
          <h1 className="font-display text-4xl mt-1">Bank holidays</h1>
          <p className="text-sm text-[#4B5773] mt-2 max-w-xl">Holidays auto-apply to employees in matching countries. Use auto-import to pull official dates for {countries.length || 250}+ countries, or add custom holidays.</p>
        </div>
        <div className="flex gap-2 flex-wrap items-center">
          <Select value={country} onValueChange={setCountry}>
            <SelectTrigger className="h-10 rounded-lg w-52" data-testid="bh-country-select"><SelectValue placeholder="Country"/></SelectTrigger>
            <SelectContent className="max-h-80">
              {countries.map(c => (
                <SelectItem key={c.code} value={c.code} data-testid={`bh-country-opt-${c.code}`}>
                  {c.name}{c.auto_import_supported ? "" : " (manual only)"}
                </SelectItem>
              ))}
            </SelectContent>
          </Select>
          <Select value={String(year)} onValueChange={(v) => setYear(Number(v))}>
            <SelectTrigger className="h-10 rounded-lg w-24" data-testid="bh-year-select"><SelectValue/></SelectTrigger>
            <SelectContent>{YEARS.map(y => <SelectItem key={y} value={String(y)}>{y}</SelectItem>)}</SelectContent>
          </Select>
          <Button onClick={() => setImportOpen(true)} disabled={!canAutoImport} data-testid="bh-import-btn"
            variant="outline" className="h-10 rounded-lg border-[#2563EB] text-[#2563EB] hover:bg-[#DBE7FE]">
            <Sparkles className="h-4 w-4 mr-1.5"/>Import official
          </Button>
          <Button onClick={rollForward} disabled={rollBusy} data-testid="bh-roll-forward-btn"
            variant="outline" className="h-10 rounded-lg">
            <FastForward className="h-4 w-4 mr-1.5"/>{rollBusy ? "Rolling…" : `Roll to ${year + 1}`}
          </Button>
          <Button onClick={() => setOpen(true)} data-testid="add-bh-btn" className="h-10 rounded-lg bg-[#2563EB] hover:bg-[#1D4ED8]">
            <Plus className="h-4 w-4 mr-1"/>Add custom
          </Button>
        </div>
      </div>

      <Card>
        <table className="w-full text-sm">
          <thead>
            <tr className="text-left text-[#4B5773]">
              <th className="py-3 eyebrow font-normal">Date</th>
              <th className="py-3 eyebrow font-normal">Name</th>
              <th className="py-3 eyebrow font-normal">Source</th>
              <th className="py-3 eyebrow font-normal text-right"></th>
            </tr>
          </thead>
          <tbody className="divide-y divide-slate-100">
            {merged.map(h => {
              // Feb 2026: honest wording so managers understand the
              // override model. Global seeds cannot be physically
              // removed (they belong to `company_id=None`), but the
              // Power toggle creates a company override row with
              // is_active=false, which hides the holiday from the
              // roster for this workspace only. Overrides of global
              // seeds get the same "workspace" wording. Truly custom
              // holidays (company row, no overrides_holiday_id) use
              // the "custom" wording so the manager knows the delete
              // action is destructive there.
              const isRealCustom = !!h.company_id && !h.overrides_holiday_id;
              const toggleTitle = h.is_active
                ? (isRealCustom ? "Disable this custom holiday" : "Disable for this workspace")
                : "Enable for this workspace";
              return (
              <tr key={h.holiday_id} className={!h.is_active ? "opacity-60" : ""}>
                <td className="py-3 font-medium">{h.date}</td>
                <td className="py-3">
                  {h.name}
                  {!h.is_active && (
                    <span className="ml-2 text-[11px] px-1.5 py-0.5 rounded bg-slate-100 text-slate-500"
                          data-testid={`bh-disabled-${h.date}`}>
                      Disabled for this workspace
                    </span>
                  )}
                </td>
                <td className="py-3">
                  <span className="text-xs px-2 py-0.5 rounded-full bg-slate-100 text-slate-600">
                    {h.company_id ? "company override" :
                     h.source === "library:holidays" ? "official library" : "global seed"}
                  </span>
                </td>
                <td className="py-3 text-right">
                  <Button size="sm" variant="ghost" onClick={() => toggle(h)}
                          title={toggleTitle}
                          data-testid={`bh-toggle-${h.date}`}>
                    <Power className={`h-4 w-4 ${h.is_active ? "text-[#2563EB]" : "text-slate-400"}`}/>
                  </Button>
                </td>
              </tr>
              );
            })}
            {!merged.length && (
              <tr><td colSpan={4} className="py-12 text-center text-[#4B5773]">
                <div className="mb-3">No holidays yet for {selectedCountry?.name || country} in {year}.</div>
                {canAutoImport && (
                  <Button size="sm" onClick={() => setImportOpen(true)} className="rounded-lg bg-[#2563EB] hover:bg-[#1D4ED8]">
                    <Download className="h-4 w-4 mr-1.5"/>Import official holidays
                  </Button>
                )}
              </td></tr>
            )}
          </tbody>
        </table>
      </Card>

      {/* Auto-import dialog */}
      <Dialog open={importOpen} onOpenChange={setImportOpen}>
        <DialogContent>
          <DialogHeader><DialogTitle>Import official holidays - {selectedCountry?.name}, {year}</DialogTitle></DialogHeader>
          <p className="text-sm text-[#4B5773]">
            Pulls the official {year} public-holiday list for {selectedCountry?.name} from the offline <code className="bg-slate-100 px-1 rounded text-xs">holidays</code> library.
            Existing dates are kept by default - use Replace to overwrite.
          </p>
          <DialogFooter className="gap-2">
            <Button variant="outline" disabled={importBusy} onClick={() => runImport(true)} data-testid="bh-import-replace-btn"
              className="rounded-lg">Replace existing</Button>
            <Button disabled={importBusy} onClick={() => runImport(false)} data-testid="bh-import-confirm-btn"
              className="rounded-lg bg-[#2563EB] hover:bg-[#1D4ED8]">
              {importBusy ? "Importing…" : "Import"}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Custom holiday dialog */}
      <Dialog open={open} onOpenChange={setOpen}>
        <DialogContent>
          <DialogHeader><DialogTitle>Add bank holiday - {selectedCountry?.name}</DialogTitle></DialogHeader>
          <form onSubmit={submit} className="space-y-4">
            <div>
              <Label className="eyebrow">Date</Label>
              <Input required type="date" value={form.date} data-testid="bh-date-input"
                onChange={(e) => setForm({ ...form, date: e.target.value })} className="mt-2 h-11 rounded-lg"/>
            </div>
            <div>
              <Label className="eyebrow">Name</Label>
              <Input required value={form.name} data-testid="bh-name-input"
                onChange={(e) => setForm({ ...form, name: e.target.value })} className="mt-2 h-11 rounded-lg"/>
            </div>
            <DialogFooter>
              <Button type="submit" data-testid="bh-submit-btn" className="rounded-lg bg-[#2563EB] hover:bg-[#1D4ED8]">Add</Button>
            </DialogFooter>
          </form>
        </DialogContent>
      </Dialog>
    </div>
  );
}
